import type { AuditMeta, Paginated, QueryParams } from '@/types'
import type { MockDB } from '@/services/mock/seed'
import { getCollection, nextId, queryCollection, setCollection, withLatency } from '@/services/mock/db'
import { createHttpResource } from '@/services/http/resource'
import { DATA_SOURCE } from '@/services/config'

interface HasId {
  id: string
}

export interface ResourceConfig {
  collection: keyof MockDB
  idPrefix: string
  searchable?: string[]
  /**
   * The real backend path for this resource (e.g. 'contacts'). Defaults to
   * `collection`. Set it when the API endpoint name differs from the mock
   * collection name — e.g. the mock 'stakeholders' maps to the API '/contacts'.
   */
  endpoint?: string
  /**
   * True once this module actually exists on the FastAPI backend. Only these
   * resources switch to HTTP when VITE_DATA_SOURCE=api; everything else stays
   * on the mock store. This lets us migrate the backend one module at a time.
   */
  backend?: boolean
  /**
   * Optional converters between the API's JSON shape and the frontend type.
   *   fromApi : raw API JSON  -> the frontend entity (used on read)
   *   toApi   : a frontend payload -> the API's JSON body (used on write)
   * When omitted, the data passes through unchanged.
   */
  fromApi?: (raw: unknown) => unknown
  toApi?: (payload: unknown) => unknown
}

/** REST-like resource surface implemented by both the mock and http backends. */
export interface Resource<T> {
  list(params?: QueryParams): Promise<Paginated<T>>
  all(): Promise<T[]>
  get(id: string): Promise<T>
  create(payload: Partial<T>): Promise<T>
  update(id: string, patch: Partial<T>): Promise<T>
  remove(id: string): Promise<{ id: string }>
  bulkRemove(ids: string[]): Promise<{ ids: string[] }>
}

/** In-browser mock implementation backed by the seeded localStorage store. */
function createMockResource<T extends HasId>(config: ResourceConfig): Resource<T> {
  const read = () => getCollection(config.collection) as unknown as T[]
  const write = (items: T[]) => setCollection(config.collection, items as unknown as MockDB[keyof MockDB])

  return {
    list: (params?: QueryParams): Promise<Paginated<T>> =>
      withLatency(() => queryCollection(read(), params, config.searchable)),

    all: (): Promise<T[]> => withLatency(() => read()),

    get: (id: string): Promise<T> =>
      withLatency(() => {
        const item = read().find((x) => x.id === id)
        if (!item) throw new Error(`${config.idPrefix} ${id} not found`)
        return item
      }),

    create: (payload: Partial<T>): Promise<T> =>
      withLatency(() => {
        const items = read()
        const nowIso = new Date().toISOString()
        const audit: AuditMeta = {
          createdAt: nowIso,
          updatedAt: nowIso,
          createdBy: 'usr-001',
          updatedBy: 'usr-001',
        }
        const created = {
          ...(payload as T),
          id: nextId(config.idPrefix, items),
          audit,
        } as T
        write([created, ...items])
        return created
      }),

    update: (id: string, patch: Partial<T>): Promise<T> =>
      withLatency(() => {
        const items = read()
        const idx = items.findIndex((x) => x.id === id)
        if (idx === -1) throw new Error(`${config.idPrefix} ${id} not found`)
        const existing = items[idx] as T & { audit?: AuditMeta }
        const updated = {
          ...existing,
          ...patch,
          id,
          audit: existing.audit
            ? { ...existing.audit, updatedAt: new Date().toISOString(), updatedBy: 'usr-001' }
            : undefined,
        } as T
        const next = [...items]
        next[idx] = updated
        write(next)
        return updated
      }),

    remove: (id: string): Promise<{ id: string }> =>
      withLatency(() => {
        write(read().filter((x) => x.id !== id))
        return { id }
      }),

    bulkRemove: (ids: string[]): Promise<{ ids: string[] }> =>
      withLatency(() => {
        write(read().filter((x) => !ids.includes(x.id)))
        return { ids }
      }),
  }
}

/**
 * Turns a resource config into an async REST-like resource. Picks the backend
 * from VITE_DATA_SOURCE — the in-browser mock store (default) or the real
 * HTTP API. The hooks and UI are identical regardless of which one is active.
 */
export function createResource<T extends HasId>(config: ResourceConfig): Resource<T> {
  // Use the real backend only when (a) the app is in API mode AND (b) this
  // particular module has been built on the backend. Otherwise stay on mock.
  const useApi = DATA_SOURCE === 'api' && config.backend === true
  return useApi ? createHttpResource<T>(config) : createMockResource<T>(config)
}
